import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "context/Cart/CartContext";
import { LoginContext } from "context/Auth/LoginContext";
import LoginModal from "Components/Modal/LoginModal";
import VerificationModal from "Components/Modal/VerificationModal";
import PendingCartModal from "Components/Modal/PendingCartModal";
import ExpiredSessionModal from "Components/Modal/ExpiredSessionModal";
import { useTranslation } from "react-i18next";
import { CheckCircle } from "@phosphor-icons/react";

const CourseSessions = ({
  data,
  isPurchased,
  setSelectedSession,
  selectedSession,
  watchedSessions = [],
  courseProgress = 0,
}) => {
  const { t } = useTranslation();
  const { addToCart, isItemInCart, isItemLoading, cart } =
    useContext(CartContext);
  const { token, isLoggedIn, isVerified, user } = useContext(LoginContext);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showPendingCartModal, setShowPendingCartModal] = useState(false);
  const [showExpiredSessionModal, setShowExpiredSessionModal] = useState(false);
  const navigate = useNavigate();

  const handleAddToCart = () => {
    if (!data) return;

    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    if (isLoggedIn && !isVerified) {
      setShowVerificationModal(true);
      return;
    }

    if (cart?.status === "pending") {
      setShowPendingCartModal(true);
      return;
    }

    const item = {
      type: "course",
      id: data._id,
      slug: data.slug,
    };

    addToCart(item, token);
  };

  const handleVerificationLater = () => {
    setShowVerificationModal(false);
  };

  const handleVerification = () => {
    navigate("/verify-email", { state: { email: user?.email } });
    setShowVerificationModal(false);
  };

  const isInCart = data && isItemInCart("course", data.slug);
  const isLoading = data && isItemLoading("course", data.slug);

  // Check if a session has been watched
  const isSessionWatched = (sessionId) => {
    return watchedSessions.some(
      (w) => w.sessionId === sessionId || w.sessionId?.toString() === sessionId
    );
  };

  return (
    <div className="w-full">
      <div className="border-b border-gray-200 mb-4 pb-2">
        <h2 className="text-lg md:text-xl font-medium text-gray-800">
          {data?.sessions?.length} {t("courses.courseSessions")}
        </h2>
      </div>

      {/* Progress bar for purchased courses */}
      {isPurchased && data?.sessions?.length > 0 && (
        <div className="mb-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              {t("courses.yourProgress")}
            </span>
            <span className="text-sm font-semibold text-primary">
              {courseProgress}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-500"
              style={{ width: `${courseProgress}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {watchedSessions.length} / {data?.sessions?.length} {t("courses.sessionsCompleted")}
          </p>
        </div>
      )}

      {/* Purchase status indicator */}
      {!isPurchased && data?.price && (
        <div className="mb-4 bg-blue-50 p-3 md:p-4 rounded-lg border border-blue-100">
          <p className="text-gray-700 text-xs md:text-sm mb-2">
            {t("courses.purchaseToAccess")}
          </p>
          {isInCart ? (
            <Link
              to="/cart"
              className="block w-full text-center bg-dark text-white px-3 md:px-4 py-2 rounded hover:bg-darkDarker transition-colors text-sm md:text-base"
            >
              {t("cart.goToCart")}
            </Link>
          ) : (
            <button
              onClick={handleAddToCart}
              disabled={isLoading}
              className={`block w-full text-center ${
                isLoading ? "bg-gray-400" : "bg-primary hover:bg-primaryDark"
              } text-white px-3 md:px-4 py-2 rounded transition-colors text-sm md:text-base`}
            >
              {isLoading
                ? t("courses.adding")
                : `${t("courses.addToCart")} - $${data?.price}`}
            </button>
          )}
        </div>
      )}

      <div className="flex flex-col space-y-2 md:space-y-3">
        {data?.sessions?.map((session, index) => {
          const {
            title,
            description,
            videoMetadata,
            videoFullData,
            videoId,
            _id,
          } = session;

          // Determine if we can show full video (purchased course and has video data)
          const canShowFullVideo = isPurchased && (videoFullData || videoId);
          const showThumbnail =
            videoMetadata?.thumbnail || videoFullData?.assets?.thumbnail;

          // Check if this session is currently selected
          const isSelected = selectedSession && selectedSession._id === _id;

          return (
            <div
              key={_id}
              className={`border ${
                isSelected ? "border-primary" : "border-gray-200"
              } rounded overflow-hidden ${
                isSelected ? "bg-blue-50" : "bg-white"
              }`}
            >
              <div
                className={`cursor-pointer ${
                  !canShowFullVideo ? "opacity-70" : ""
                }`}
                onClick={() =>
                  canShowFullVideo ? setSelectedSession(session) : null
                }
              >
                <div className="flex items-start p-2 md:p-3">
                  {/* Left Side - Thumbnail */}
                  <div className="w-20 h-14 md:w-24 md:h-16 relative mr-2 md:mr-3 flex-shrink-0">
                    {showThumbnail ? (
                      <div className="relative h-full w-full">
                        <img
                          src={
                            videoMetadata?.thumbnail ||
                            videoFullData?.assets?.thumbnail
                          }
                          alt={title}
                          className="w-full h-full object-cover rounded"
                        />
                        {canShowFullVideo && (
                          <div className="absolute inset-0 bg-black bg-opacity-25 flex items-center justify-center rounded">
                            <div
                              className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center ${
                                isSelected
                                  ? "bg-primary"
                                  : "bg-white bg-opacity-80"
                              }`}
                            >
                              <span
                                className={`text-xs md:text-sm ${
                                  isSelected ? "text-white" : "text-primary"
                                }`}
                              >
                                ▶
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center rounded">
                        <span className="text-xs text-gray-500">
                          {videoId ? t("common.loading") : t("courses.noVideo")}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Right Side - Content */}
                  <div className="flex-1">
                    <h3
                      className={`font-medium text-sm md:text-base ${
                        isSelected ? "text-primary" : "text-gray-800"
                      }`}
                    >
                      {title}
                    </h3>
                    <p className="text-xs md:text-xs text-gray-500 mt-1 line-clamp-2">
                      {description}
                    </p>

                    <div className="mt-1 md:mt-2 flex items-center text-xs">
                      {videoMetadata?.duration && (
                        <span className="text-gray-500 mr-2 md:mr-3 text-xs">
                          {Math.floor(videoMetadata.duration / 60)}:
                          {String(
                            Math.floor(videoMetadata.duration % 60)
                          ).padStart(2, "0")}
                        </span>
                      )}
                      {canShowFullVideo ? (
                        <>
                          {isSessionWatched(_id) && !isSelected && (
                            <span className="inline-flex items-center gap-1 px-1.5 md:px-2 py-0.5 rounded-full bg-green-500 text-white text-xs mr-1">
                              <CheckCircle size={12} weight="fill" />
                              {t("courses.completed")}
                            </span>
                          )}
                          <span
                            className={`inline-block px-1.5 md:px-2 py-0.5 rounded-full text-xs ${
                              isSelected
                                ? "bg-primary text-white"
                                : isSessionWatched(_id)
                                ? "bg-gray-100 text-gray-600"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {isSelected
                              ? t("courses.nowPlaying")
                              : isSessionWatched(_id)
                              ? t("courses.watchAgain")
                              : t("courses.available")}
                          </span>
                        </>
                      ) : (
                        <span className="inline-block px-1.5 md:px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 text-xs">
                          {t("courses.locked")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modals */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
      <VerificationModal
        isOpen={showVerificationModal}
        onClose={handleVerificationLater}
        onVerify={handleVerification}
        email={user?.email}
      />
      <PendingCartModal
        isOpen={showPendingCartModal}
        onClose={() => setShowPendingCartModal(false)}
      />
      <ExpiredSessionModal
        isOpen={showExpiredSessionModal}
        onClose={() => setShowExpiredSessionModal(false)}
      />

      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default CourseSessions;
